import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { contactInfo } from "../data/mock";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Menu, ArrowRight } from "lucide-react";
import { scrollToId } from "../lib/motion";

const navLinks = [
  { label: "How I Help", href: "/#improve", type: "anchor" },
  { label: "Business Diagnostic", href: "/diagnostic", type: "route" },
  { label: "Examples", href: "/#examples", type: "anchor" },
  { label: "Results", href: "/#results", type: "anchor" },
  { label: "About", href: "/about", type: "route" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (link) => {
    setOpen(false);
    if (link.type === "route") {
      navigate(link.href);
    } else {
      const hash = link.href.split("#")[1];
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(hash), 250);
      } else {
        scrollToId(hash);
      }
    }
  };

  const isActive = (link) => link.type === "route" && location.pathname === link.href;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FAF7F2]/85 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] border-b border-stone-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-4">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 group"
            data-testid="header-logo"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_teqie-new/artifacts/on53b2n6_Teqie%20Logo.webp"
              alt="Teqie"
              className="h-10 w-auto transition-transform duration-200 group-hover:scale-105"
            />
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                  isActive(link)
                    ? "text-brand-700 bg-brand-100"
                    : "text-stone-600 hover:text-stone-900 hover:bg-stone-900/5"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={() => window.open(contactInfo.bookingUrl, "_blank", "noopener,noreferrer")}
              data-testid="header-book-conversation-btn"
              className="bg-stone-900 hover:bg-brand-700 text-[#FAF7F2] rounded-full h-10 px-5 text-sm font-medium transition-colors duration-300"
            >
              Book a Conversation
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-stone-700" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-[#FAF7F2] p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  <div className="p-6 border-b border-stone-200/70">
                    <img
                      src="https://customer-assets.emergentagent.com/job_teqie-new/artifacts/on53b2n6_Teqie%20Logo.webp"
                      alt="Teqie"
                      className="h-8 w-auto"
                    />
                  </div>
                  <nav className="flex flex-col p-4 gap-1">
                    {navLinks.map((link) => (
                      <button
                        key={link.href}
                        onClick={() => handleNav(link)}
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                          isActive(link)
                            ? "text-brand-700 bg-brand-100"
                            : "text-stone-600 hover:text-stone-900 hover:bg-stone-900/5"
                        }`}
                      >
                        {link.label}
                      </button>
                    ))}
                  </nav>
                  <div className="mt-auto p-6">
                    <Button
                      onClick={() => { setOpen(false); window.open(contactInfo.bookingUrl, "_blank", "noopener,noreferrer"); }}
                      data-testid="mobile-book-conversation-btn"
                      className="w-full bg-stone-900 hover:bg-brand-700 text-[#FAF7F2] rounded-full h-11"
                    >
                      Book a Conversation
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
